import {
  TICKET_WIDTH,
  doubleSeparator,
  separatorLine,
  centerText,
  formatLine,
} from './ticketFormatter';
import { formatOrderNumber } from './orderNumberFormatter';

export interface CategoryTicket {
  categoryId: string;
  categoryName: string;
  content: string;
}

export interface SplitTicketResult {
  orderNumber: string;
  orderDate: string;
  orderType: 'takeaway' | 'dine-in';
  tableNumber?: string;
  customerName?: string;
  tickets: CategoryTicket[];
  fullTicket: string;
}

export interface TicketMetadata {
  orderNumber: string;
  orderDate: string;
  orderType: 'takeaway' | 'dine-in';
  tableNumber?: string;
  customerName?: string;
}

const CAT_MARKER_REGEX = /\[CAT:([^|]+)\|([^:]+):START\]([\s\S]*?)\[CAT:\1\|\2:END\]/g;

export const buildMiniHeader = (
  metadata: TicketMetadata,
  categoryName: string
): string => {
  const lines: string[] = [];
  const orderTypeStr = metadata.orderType === 'takeaway' ? 'A emporter' : 'Sur place';
  const tableStr = metadata.tableNumber ? ` - Table ${metadata.tableNumber}` : '';

  lines.push(doubleSeparator());
  lines.push('\x1B\x21\x18');
  lines.push(centerText(`**** #${formatOrderNumber(metadata.orderNumber)} ****`));
  lines.push('\x1B\x21\x00');
  lines.push(doubleSeparator());
  lines.push(formatLine('Date:', metadata.orderDate));
  lines.push(formatLine('Type:', `${orderTypeStr}${tableStr}`));
  if (metadata.customerName) {
    lines.push(formatLine('Client:', metadata.customerName));
  }
  lines.push(separatorLine('-'));
  lines.push(centerText(`[ ${categoryName.toUpperCase()} ]`));
  lines.push(separatorLine('-'));

  return lines.join('\n');
};

export const buildMiniFooter = (): string => {
  const lines: string[] = [];
  lines.push(separatorLine('-'));
  lines.push(centerText('--- FIN ---'));
  lines.push(doubleSeparator());
  lines.push('\n\n\n');
  return lines.join('\n');
};

export const extractCategorySections = (
  ticketContent: string
): Array<{ categoryId: string; categoryName: string; content: string }> => {
  console.log('[extractCategorySections] Starting extraction...');
  console.log('[extractCategorySections] Ticket content length:', ticketContent.length);
  console.log('[extractCategorySections] Looking for pattern:', CAT_MARKER_REGEX.source);

  // Show first 500 chars to see if markers exist
  console.log('[extractCategorySections] Ticket preview (first 500 chars):', ticketContent.substring(0, 500));

  const sections: Array<{ categoryId: string; categoryName: string; content: string }> = [];
  let match;

  const regex = new RegExp(CAT_MARKER_REGEX.source, 'g');
  while ((match = regex.exec(ticketContent)) !== null) {
    console.log('[extractCategorySections] ✓ Found category section:', {
      categoryId: match[1],
      categoryName: match[2],
      contentLength: match[3].trim().length
    });

    sections.push({
      categoryId: match[1],
      categoryName: match[2],
      content: match[3].trim(),
    });
  }

  console.log('[extractCategorySections] Total sections found:', sections.length);
  if (sections.length === 0) {
    console.warn('[extractCategorySections] ⚠ NO CATEGORIES FOUND! Check if ticket contains [CAT:id|name:START] markers');
  }

  return sections;
};

export const stripAllMarkers = (content: string): string => {
  return content
    .replace(/\[HEADER:START\]/g, '')
    .replace(/\[HEADER:END\]/g, '')
    .replace(/\[ITEMS:START\]/g, '')
    .replace(/\[ITEMS:END\]/g, '')
    .replace(/\[TOTALS:START\]/g, '')
    .replace(/\[TOTALS:END\]/g, '')
    .replace(/\[FOOTER:START\]/g, '')
    .replace(/\[FOOTER:END\]/g, '')
    .replace(/\[CAT:[^\]]+:START\]/g, '')
    .replace(/\[CAT:[^\]]+:END\]/g, '')
    .replace(/^\s*\n/gm, '');
};

export const splitTicketByCategory = (
  ticketContent: string,
  metadata: TicketMetadata
): SplitTicketResult => {
  const categorySections = extractCategorySections(ticketContent);
  const tickets: CategoryTicket[] = [];

  for (const section of categorySections) {
    const cleanContent = stripAllMarkers(section.content);
    const miniHeader = buildMiniHeader(metadata, section.categoryName);
    const miniFooter = buildMiniFooter();

    tickets.push({
      categoryId: section.categoryId,
      categoryName: section.categoryName,
      content: miniHeader + '\n' + cleanContent + '\n' + miniFooter,
    });
  }

  return {
    orderNumber: metadata.orderNumber,
    orderDate: metadata.orderDate,
    orderType: metadata.orderType,
    tableNumber: metadata.tableNumber,
    customerName: metadata.customerName,
    tickets,
    fullTicket: stripAllMarkers(ticketContent),
  };
};

export const buildAndroidPayload = (splitResult: SplitTicketResult): string => {
  return JSON.stringify({
    orderNumber: splitResult.orderNumber,
    orderDate: splitResult.orderDate,
    orderType: splitResult.orderType,
    tableNumber: splitResult.tableNumber,
    customerName: splitResult.customerName,
    ticketCount: splitResult.tickets.length,
    tickets: splitResult.tickets.map((t) => ({
      categoryId: t.categoryId,
      categoryName: t.categoryName,
      content: t.content,
    })),
  });
};
